import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Table, Button, Alert, Spin } from 'antd';
import { RootState } from '../../store/store';
import apiClient from '@skygroup/shared/API/apiClient';
import endpoints from '../../utils/endpoints';
import {
  ChangeNotificationPublic,
  NotificationStatus,
  buildFlightChangeDiff,
  DiffField,
} from './changeApprovalTypes';
import './ChangeApprovalPage.less';

function collectBrowserData() {
  return {
    screen_width:    window.screen.width,
    screen_height:   window.screen.height,
    language:        navigator.language,
    timezone:        Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform:        navigator.platform,
    cookies_enabled: navigator.cookieEnabled,
  };
}

function formatExpiry(expiresAt: string, t: any): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return '';
  const totalMinutes = Math.floor(diff / 60000);
  const days    = Math.floor(totalMinutes / 1440);
  const hours   = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0)    parts.push(`${days} ${t.days}`);
  if (hours > 0)   parts.push(`${hours} ${t.hours}`);
  if (minutes > 0) parts.push(`${minutes} ${t.minutes}`);
  return parts.join(' ');
}

const ChangeApprovalPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { translations, direction } = useSelector((state: RootState) => state.config);
  const t: any = translations?.changeApproval || {};

  const [notification, setNotification] = useState<ChangeNotificationPublic | null>(null);
  const [localStatus, setLocalStatus] = useState<NotificationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<string>('');

  const BASE = endpoints.CHANGE_APPROVAL_PUBLIC;

  const recordOpen = useCallback(async () => {
    try {
      await apiClient.post(`${BASE}/${uuid}/open`);
    } catch {
      // non-fatal
    }
  }, [uuid, BASE]);

  const loadNotification = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${BASE}/${uuid}`);
      const data: ChangeNotificationPublic = res?.data?.data ?? res?.data;
      setNotification(data);
      setLocalStatus(data.status);
    } catch {
      setErrorMsg(t.errorBanner || 'Error loading page');
    }
    setLoading(false);
  }, [uuid, BASE, t.errorBanner]);

  useEffect(() => {
    if (!uuid) return;
    recordOpen().then(() => loadNotification());
  }, [uuid]);

  useEffect(() => {
    if (!notification?.expires_at) return;
    const tick = () => {
      const ct = formatExpiry(notification.expires_at!, t);
      setCountdown(ct);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [notification?.expires_at, t]);

  const handleAction = async (action: 'confirm' | 'decline') => {
    setActionLoading(true);
    setErrorMsg(null);
    try {
      const res = await apiClient.post(`${BASE}/${uuid}/${action}`, {
        browser_data: collectBrowserData(),
      });
      // Populate the confirmed/declined timestamp locally so the success
      // message renders it immediately (use the server value if returned).
      const updated = res?.data?.data ?? res?.data;
      const nowIso = new Date().toISOString();
      setNotification((prev) =>
        prev
          ? {
              ...prev,
              confirmed_at:
                action === 'confirm'
                  ? updated?.confirmed_at ?? nowIso
                  : prev.confirmed_at,
              declined_at:
                action === 'decline'
                  ? updated?.declined_at ?? nowIso
                  : prev.declined_at,
            }
          : prev,
      );
      setLocalStatus(
        action === 'confirm' ? NotificationStatus.CONFIRMED : NotificationStatus.DECLINED,
      );
    } catch {
      setErrorMsg(t.errorBanner || 'An error occurred. Please try again.');
    }
    setActionLoading(false);
  };

  const isExpired =
    notification?.expires_at && new Date(notification.expires_at) < new Date();

  const effectiveStatus = isExpired
    ? NotificationStatus.EXPIRED
    : (localStatus ?? notification?.status ?? NotificationStatus.PENDING);

  if (loading) {
    return (
      <div className="change-approval-page">
        <div className="status-page">
          <Spin size="large" />
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (
    effectiveStatus === NotificationStatus.CONFIRMED ||
    effectiveStatus === NotificationStatus.DECLINED ||
    effectiveStatus === NotificationStatus.EXPIRED ||
    effectiveStatus === NotificationStatus.FAILED
  ) {
    const statusConfig: Record<string, { icon: string; title: string; msg: string; cls: string }> = {
      [NotificationStatus.CONFIRMED]: { icon: '✓', title: t.confirmedTitle, msg: notification?.confirmed_at ? `${t.confirmedMsg} ${new Date(notification.confirmed_at).toLocaleDateString()}` : t.confirmedMsg, cls: 'confirmed' },
      [NotificationStatus.DECLINED]:  { icon: '✗', title: t.declinedTitle,  msg: t.declinedMsg,  cls: 'declined' },
      [NotificationStatus.EXPIRED]:   { icon: '⏰', title: t.expiredTitle,   msg: t.expiredMsg,   cls: 'expired' },
      [NotificationStatus.FAILED]:    { icon: '⚠',  title: t.failedTitle,   msg: t.failedMsg,    cls: 'failed' },
    };
    const cfg = statusConfig[effectiveStatus];
    return (
      <div className="change-approval-page">
        <div className={`status-page status-page-${cfg.cls}`}>
          <div className="status-icon">{cfg.icon}</div>
          <h2 className="status-title">{cfg.title}</h2>
          <p className="status-msg">{cfg.msg}</p>
        </div>
      </div>
    );
  }

  const diff: DiffField[] = notification?.diff_fields
    ?? buildFlightChangeDiff(notification?.old_data ?? null, notification?.new_data ?? null);

  const columns = [
    {
      title: t.colField,
      key: 'field',
      render: (_: any, record: DiffField) => (
        <span>
          <span className="diff-label-he">{record.labelHe}</span>
          <span className="diff-label-sep"> / </span>
          <span className="diff-label-en">{record.labelEn}</span>
        </span>
      ),
    },
    {
      title: t.colBefore,
      dataIndex: 'old',
      key: 'old',
      render: (value: string, record: DiffField) =>
        record.changed ? <span className="diff-old-changed">{value}</span> : <span>{value}</span>,
    },
    {
      title: t.colAfter,
      dataIndex: 'new',
      key: 'new',
      render: (value: string, record: DiffField) =>
        record.changed ? <span className="diff-new-changed">{value}</span> : <span>{value}</span>,
    },
  ];

  const airlineName   = notification?.new_data?.airline_name  ?? notification?.old_data?.airline_name;
  const airlineLogo   = notification?.new_data?.airline_logo  ?? notification?.old_data?.airline_logo;
  const flightNumber  = notification?.new_data?.flight_number ?? notification?.old_data?.flight_number;

  return (
    <div className="change-approval-page" dir={direction || 'rtl'}>
      <div className="cap-container">
        <div className="cap-header">
          {airlineLogo && (
            <img src={airlineLogo} alt={airlineName || ''} className="airline-logo" />
          )}
          {airlineName && <span className="airline-name">{airlineName}</span>}
          {flightNumber && <span className="flight-number">{flightNumber}</span>}
        </div>

        <div className="cap-greeting">
          <h2>
            {t.greeting}
            {notification?.customer_name ? ` ${notification.customer_name}` : ''}
          </h2>
          <p className="cap-subtitle">{t.subtitle}</p>
        </div>

        <div className="cap-diff-section">
          <h3 className="cap-section-title">{t.changeTableTitle}</h3>
          <Table
            className="diff-table"
            rowClassName={(record: DiffField) => record.changed ? 'diff-row-changed' : ''}
            pagination={false}
            rowKey="labelEn"
            columns={columns}
            dataSource={diff}
            size="small"
          />
        </div>

        {errorMsg && (
          <Alert
            className="cap-error"
            type="error"
            message={errorMsg}
            showIcon
            closable
            onClose={() => setErrorMsg(null)}
          />
        )}

        <div className="cap-prompt">
          <p>{t.confirmPrompt}</p>
        </div>

        <div className="cap-actions">
          <Button
            className="btn-confirm"
            loading={actionLoading}
            onClick={() => handleAction('confirm')}
            block
          >
            ✓ {t.confirmBtn}
          </Button>
          <Button
            className="btn-decline"
            loading={actionLoading}
            onClick={() => handleAction('decline')}
            block
          >
            ✗ {t.declineBtn}
          </Button>
        </div>

        <div className="cap-expiry">
          {notification?.expires_at && (
            <span>
              {t.expiresLabel}:{' '}
              {new Date(notification.expires_at).toLocaleDateString()}
            </span>
          )}
          {countdown && (
            <span className="cap-countdown">
              {' '}⏰ {t.expiresIn} {countdown}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeApprovalPage;
